import { Q } from '@nozbe/watermelondb';
import { getDemoDatabase } from './index';
import { normalizeEmail } from '../../utils/normalizeEmail';
import DemoProduct from './models/DemoProduct';
import DemoOrderMaster from './models/DemoOrderMaster';
import DemoPaymentInformation from './models/DemoPaymentInformation';
import DemoSaleInformation from './models/DemoSaleInformation';

export interface DemoCartCheckoutItem {
  product_id: string;
  variant_id?: string;
  quantity: number;
  mass: number;
  unit_price: number;
  price_type: 'item' | 'unit';
  name: string;
  unit_of_mass: string;
  category: string;
  image: string;
}

export interface DemoProductInformationSnapshot {
  _id: string;
  bp_owner_id: string;
  bp_product_location_id: string;
  bp_product_barcode: string;
  bp_product_name: string;
  bp_product_category: string;
  bp_product_sub_category: string;
  bp_product_price_type: 'item' | 'unit';
  bp_product_unit_of_mass: string;
  bp_product_mass: string;
  bp_product_buying_price: number;
  bp_product_selling_price: number;
  bp_product_quantity: number;
  bp_product_size: string;
  bp_minimum_stock_level: number;
  bp_product_model: string;
  bp_product_image: string;
  bp_product_deleted: boolean;
  bp_product_is_favourite: boolean;
  bp_product_created_at: number;
  bp_product_updated_at: number;
  bp_variant_id?: string;
  bp_variant_attributes?: Record<string, string>;
  bp_product_has_variants?: boolean;
  bp_product_parent_name?: string;
}

export interface DemoSalesProductInformation {
  bp_sales_id: string;
  bp_employee_id: string;
  bp_owner_id: string;
  bp_order_id: string;
  bp_product_id: string;
  bp_order_quantity: number;
  bp_order_price: number;
  bp_order_paid_quantity: number;
  bp_order_completed: boolean;
  bp_order_partial_paid: boolean;
  bp_order_created_at: number;
  bp_order_updated_at: number;
  bp_order_deleted_at: number;
  bp_order_mass: number;
  bp_order_paid_mass: number;
  bp_sales_location_id: string;
  bp_selling_price_changed: boolean;
  bp_order_price_type: 'item' | 'unit';
  bp_product_information: DemoProductInformationSnapshot;
}

export interface DemoPaymentInformationObject {
  _id: string;
  bp_payment_status: boolean;
  bp_payment_amount: number;
  bp_payment_amount_received: number;
  bp_payment_already_paid_amount: number;
  bp_payment_amount_actually_received: number;
  bp_payment_amount_due: number;
  bp_payment_amount_refunded: number;
  bp_payment_refunds: DemoPaymentRefundEntry[];
  bp_order_information_id: string;
  bp_payment_created_at: number;
  bp_payment_updated_at: number;
  bp_payment_deleted_at: number;
  bp_payments: DemoPaymentEntry[];
  bp_payment_location_id: string;
  bp_payment_method: string;
}

export interface DemoPaymentEntry {
  method: string;
  amount: number;
  timestamp: number;
}

export interface DemoPaymentRefundEntry {
  payment_id?: string;
  reason?: string;
  amount: number;
  timestamp: number;
}

export interface DemoHydratedOrder {
  bp_order_id: string;
  bp_order_master_owner_id: string;
  bp_order_master_employee_id: string;
  bp_order_customer_information: string;
  bp_order_customer_email_information: string;
  bp_order_customer_phone_number_information: string;
  bp_order_customer_information_updated_at: number;
  bp_order_master_order_status: 'COMPLETED' | 'PENDING' | 'HOLD' | 'PARTIAL';
  bp_order_master_payment_information: DemoPaymentInformationObject;
  bp_order_master_receipt_reference_id: string;
  bp_order_master_receipt_ref_id_updated_at: number;
  bp_sales_products_information: DemoSalesProductInformation[];
  bp_order_master_created_at: number;
  bp_order_master_updated_at: number;
  bp_order_master_deleted_at: number;
  bp_order_master_location_id: string;
  bp_order_master_order_total: number;
  bp_total_sales_products: number;
}

export interface DemoCheckoutNavigationPayload {
  order: DemoSalesProductInformation[];
  orderID: string;
  Payment_Information: DemoPaymentInformationObject;
  ReceiptID: string;
}

export interface CreateDemoOrderParams {
  cartItems: DemoCartCheckoutItem[];
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  businessName?: string;
  ownerId?: string;
  employeeId?: string;
  locationId?: string;
  paymentMethod?: 'Cash' | 'Mpesa';
}

const DEFAULT_OWNER_ID = 'demo_owner';
const DEFAULT_LOCATION_ID = 'demo_location';
const DEFAULT_EMPLOYEE_ID = '';
const DEFAULT_BUSINESS_NAME = 'demo location';

function normalizePriceType(value: string): 'item' | 'unit' {
  if (value === 'unit' || value === 'per_mass') {
    return 'unit';
  }
  return 'item';
}

function toLineTotal(item: DemoCartCheckoutItem): number {
  const qty = item.price_type === 'unit' ? item.mass : item.quantity;
  return Number((item.unit_price * Math.max(0, qty)).toFixed(3));
}

async function generateUniqueReceiptReferenceId(
  businessName: string,
): Promise<string> {
  const db = getDemoDatabase();
  const orderCollection = db.get<DemoOrderMaster>(
    'demo_order_master_information',
  );
  const businessPrefix = businessName
    ? businessName.substring(0, 2).toUpperCase()
    : 'BP';
  const maxAttempts = 10;
  let attempt = 0;

  while (attempt < maxAttempts) {
    try {
      const randomPart = Math.floor(100000000 + Math.random() * 900000000);
      const receiptReferenceId = `${businessPrefix}${randomPart}`;

      const existingCount = await orderCollection
        .query(
          Q.where('bp_order_master_receipt_reference_id', receiptReferenceId),
        )
        .fetchCount();

      if (existingCount === 0) {
        return receiptReferenceId;
      }
    } catch (error) {
      console.error('Error checking receipt reference ID:', error);
    }
    attempt += 1;
  }

  throw new Error(
    'Failed to generate a unique receipt reference ID after multiple attempts',
  );
}

function buildFallbackProductSnapshot(
  item: DemoCartCheckoutItem,
  ownerId: string,
  locationId: string,
  now: number,
): DemoProductInformationSnapshot {
  return {
    _id: item.product_id,
    bp_owner_id: ownerId,
    bp_product_location_id: locationId,
    bp_product_barcode: '',
    bp_product_name: item.name,
    bp_product_category: item.category,
    bp_product_sub_category: item.category,
    bp_product_price_type: item.price_type,
    bp_product_unit_of_mass: item.unit_of_mass,
    bp_product_mass: item.price_type === 'unit' ? String(item.mass) : '',
    bp_product_buying_price: item.unit_price,
    bp_product_selling_price: item.unit_price,
    bp_product_quantity: 0,
    bp_product_size: '',
    bp_minimum_stock_level: 0,
    bp_product_model: '',
    bp_product_image: item.image,
    bp_product_deleted: false,
    bp_product_is_favourite: false,
    bp_product_created_at: now,
    bp_product_updated_at: now,
    bp_variant_id: item.variant_id,
    bp_product_has_variants: Boolean(item.variant_id),
  };
}

function applyCartLineToSnapshot(
  snapshot: DemoProductInformationSnapshot,
  item: DemoCartCheckoutItem,
  variantAttributes?: Record<string, string>,
): DemoProductInformationSnapshot {
  return {
    ...snapshot,
    bp_product_name: item.name || snapshot.bp_product_name,
    bp_product_selling_price: item.unit_price,
    bp_product_image: item.image || snapshot.bp_product_image,
    bp_variant_id: item.variant_id,
    bp_variant_attributes: variantAttributes,
    bp_product_has_variants: Boolean(
      item.variant_id || snapshot.bp_product_has_variants,
    ),
    bp_product_parent_name: item.variant_id
      ? snapshot.bp_product_name
      : undefined,
  };
}

function mapProductToSnapshot(
  product: DemoProduct,
): DemoProductInformationSnapshot {
  return {
    _id: product.id,
    bp_owner_id: product.bpOwnerId,
    bp_product_location_id: product.bpProductLocationId,
    bp_product_barcode: product.bpProductBarcode,
    bp_product_name: product.bpProductName,
    bp_product_category: product.bpProductCategory,
    bp_product_sub_category: product.bpProductSubCategory,
    bp_product_price_type: normalizePriceType(product.bpProductPriceType),
    bp_product_unit_of_mass: product.bpProductUnitOfMass,
    bp_product_mass: product.bpProductMass,
    bp_product_buying_price: Number(product.bpProductBuyingPrice ?? 0),
    bp_product_selling_price: Number(product.bpProductSellingPrice ?? 0),
    bp_product_quantity: Number(product.bpProductQuantity ?? 0),
    bp_product_size: product.bpProductSize,
    bp_minimum_stock_level: product.bpMinimumStockLevel,
    bp_product_model: product.bpProductModel,
    bp_product_image: product.bpProductImage,
    bp_product_deleted: product.bpProductDeleted,
    bp_product_is_favourite: product.bpProductIsFavourite,
    bp_product_created_at: product.bpProductCreatedAt,
    bp_product_updated_at: product.bpProductUpdatedAt,
    bp_product_has_variants: Boolean(product.bpProductHasVariants),
  };
}

function cartLineSnapshotKey(item: DemoCartCheckoutItem): string {
  return `${item.product_id}::${item.variant_id ?? ''}`;
}

async function loadProductSnapshots(
  cartItems: DemoCartCheckoutItem[],
  ownerId: string,
  locationId: string,
  now: number,
): Promise<Map<string, DemoProductInformationSnapshot>> {
  const db = getDemoDatabase();
  const productsCollection = db.get<DemoProduct>('demo_products');
  const productMap = new Map<string, DemoProductInformationSnapshot>();

  for (const item of cartItems) {
    const key = cartLineSnapshotKey(item);
    try {
      const product = await productsCollection.find(item.product_id);
      const base = mapProductToSnapshot(product);
      const variants = product.getVariants();
      const matched = item.variant_id
        ? variants.find(v => v._id === item.variant_id)
        : null;
      productMap.set(
        key,
        applyCartLineToSnapshot(
          base,
          item,
          matched?.bp_variant_attributes,
        ),
      );
    } catch {
      productMap.set(
        key,
        buildFallbackProductSnapshot(item, ownerId, locationId, now),
      );
    }
  }

  return productMap;
}

function toHydratedOrder(
  order: DemoOrderMaster,
  payment: DemoPaymentInformationObject,
  sales: DemoSalesProductInformation[],
): DemoHydratedOrder {
  return {
    bp_order_id: order.id,
    bp_order_master_owner_id: order.bpOrderMasterOwnerId,
    bp_order_master_employee_id: order.bpOrderMasterEmployeeId,
    bp_order_customer_information: order.bpOrderCustomerInformation,
    bp_order_customer_email_information: order.bpOrderCustomerEmailInformation,
    bp_order_customer_phone_number_information:
      order.bpOrderCustomerPhoneNumberInformation,
    bp_order_customer_information_updated_at:
      order.bpOrderCustomerInformationUpdatedAt,
    bp_order_master_order_status: order.bpOrderMasterOrderStatus as
      | 'COMPLETED'
      | 'PENDING'
      | 'HOLD'
      | 'PARTIAL',
    bp_order_master_payment_information: payment,
    bp_order_master_receipt_reference_id: order.bpOrderMasterReceiptReferenceId,
    bp_order_master_receipt_ref_id_updated_at:
      order.bpOrderMasterReceiptRefIdUpdatedAt,
    bp_sales_products_information: sales,
    bp_order_master_created_at: order.bpOrderMasterCreatedAt,
    bp_order_master_updated_at: order.bpOrderMasterUpdatedAt,
    bp_order_master_deleted_at: order.bpOrderMasterDeletedAt,
    bp_order_master_location_id: order.bpOrderMasterLocationId,
    bp_order_master_order_total: order.bpOrderMasterOrderTotal,
    bp_total_sales_products: sales.length,
  };
}

function mapSaleModelToSalesInformation(
  sale: DemoSaleInformation,
): DemoSalesProductInformation {
  const raw = sale.toSalesObject();
  return {
    ...raw,
    bp_order_price_type: raw.bp_order_price_type === 'unit' ? 'unit' : 'item',
    bp_product_information:
      raw.bp_product_information as unknown as DemoProductInformationSnapshot,
  };
}

export function toDemoCheckoutNavigationPayload(
  order: DemoHydratedOrder,
): DemoCheckoutNavigationPayload {
  return {
    order: order.bp_sales_products_information,
    orderID: order.bp_order_id,
    Payment_Information: order.bp_order_master_payment_information,
    ReceiptID: order.bp_order_master_receipt_reference_id ?? '',
  };
}

export async function createDemoOrderInformation(
  params: CreateDemoOrderParams,
): Promise<{
  order: DemoHydratedOrder;
  checkoutPayload: DemoCheckoutNavigationPayload;
}> {
  const now = Date.now();
  const ownerId = params.ownerId || DEFAULT_OWNER_ID;
  const locationId = params.locationId || DEFAULT_LOCATION_ID;
  const employeeId = params.employeeId || DEFAULT_EMPLOYEE_ID;
  const customerName = (params.customerName || 'Walk in customer').trim();
  const customerEmail = normalizeEmail(params.customerEmail || '');
  const customerPhone = (params.customerPhone || '').trim();
  const businessName = (params.businessName || DEFAULT_BUSINESS_NAME).trim();
  const paymentMethod = params.paymentMethod || 'Cash';

  const validItems = params.cartItems
    .map(item => ({
      ...item,
      quantity:
        item.price_type === 'unit' ? 0 : Math.max(0, Math.floor(item.quantity)),
      mass: item.price_type === 'unit' ? Math.max(0, item.mass) : 0,
    }))
    .filter(item =>
      item.price_type === 'unit' ? item.mass > 0 : item.quantity > 0,
    );

  if (validItems.length === 0) {
    throw new Error('No valid cart items to checkout');
  }

  const orderTotal = Number(
    validItems.reduce((sum, item) => sum + toLineTotal(item), 0).toFixed(3),
  );
  const receiptReference = await generateUniqueReceiptReferenceId(businessName);
  const productSnapshots = await loadProductSnapshots(
    validItems,
    ownerId,
    locationId,
    now,
  );

  const db = getDemoDatabase();
  const orderCollection = db.get<DemoOrderMaster>(
    'demo_order_master_information',
  );
  const paymentCollection = db.get<DemoPaymentInformation>(
    'demo_payment_information',
  );
  const salesCollection = db.get<DemoSaleInformation>('demo_sales_information');

  let createdOrderId = '';
  let createdPaymentId = '';
  const createdSalesRecords: DemoSaleInformation[] = [];

  await db.write(async () => {
    const createdPaymentRecord = await paymentCollection.create(
      (payment: DemoPaymentInformation) => {
        payment.bpPaymentStatus = false;
        payment.bpPaymentAmount = orderTotal;
        payment.bpPaymentAmountReceived = 0;
        payment.bpPaymentAlreadyPaidAmount = 0;
        payment.bpPaymentAmountActuallyReceived = 0;
        payment.bpPaymentAmountDue = orderTotal;
        payment.bpPaymentAmountRefunded = 0;
        payment.bpPaymentRefunds = JSON.stringify([]);
        payment.bpOrderInformationId = '';
        payment.bpPaymentCreatedAt = now;
        payment.bpPaymentUpdatedAt = now;
        payment.bpPaymentDeletedAt = 0;
        payment.bpPayments = JSON.stringify([]);
        payment.bpPaymentLocationId = locationId;
        payment.bpPaymentMethod = paymentMethod;
      },
    );
    createdPaymentId = createdPaymentRecord.id;

    const createdOrderRecord = await orderCollection.create(
      (order: DemoOrderMaster) => {
        order.bpOrderMasterOwnerId = ownerId;
        order.bpOrderMasterEmployeeId = employeeId;
        order.bpOrderCustomerInformation = customerName;
        order.bpOrderCustomerEmailInformation = customerEmail;
        order.bpOrderCustomerPhoneNumberInformation = customerPhone;
        order.bpOrderCustomerInformationUpdatedAt = now;
        order.bpOrderMasterOrderStatus = 'PENDING';
        order.bpOrderMasterPaymentInformation = createdPaymentRecord.id;
        order.bpOrderMasterReceiptReferenceId = receiptReference;
        order.bpOrderMasterReceiptRefIdUpdatedAt = now;
        order.bpSalesProductsInformation = JSON.stringify([]);
        order.bpOrderMasterCreatedAt = now;
        order.bpOrderMasterUpdatedAt = now;
        order.bpOrderMasterDeletedAt = 0;
        order.bpOrderMasterLocationId = locationId;
        order.bpOrderMasterOrderTotal = orderTotal;
      },
    );
    createdOrderId = createdOrderRecord.id;

    for (const item of validItems) {
      const productInformation =
        productSnapshots.get(cartLineSnapshotKey(item)) ||
        buildFallbackProductSnapshot(item, ownerId, locationId, now);

      const sale = await salesCollection.create(
        (sales: DemoSaleInformation) => {
          sales.bpEmployeeId = employeeId;
          sales.bpOwnerId = ownerId;
          sales.bpOrderId = createdOrderRecord.id;
          sales.bpProductId = item.product_id;
          sales.bpOrderQuantity = item.quantity;
          sales.bpOrderPrice = toLineTotal(item);
          sales.bpOrderPaidQuantity = 0;
          sales.bpOrderCompleted = false;
          sales.bpOrderPartialPaid = false;
          sales.bpOrderCreatedAt = now;
          sales.bpOrderUpdatedAt = now;
          sales.bpOrderDeletedAt = 0;
          sales.bpOrderMass = item.mass;
          sales.bpOrderPaidMass = 0;
          sales.bpSalesLocationId = locationId;
          sales.bpSellingPriceChanged = false;
          sales.bpProductInformation = JSON.stringify(productInformation);
          sales.bpOrderPriceType = item.price_type;
        },
      );
      createdSalesRecords.push(sale);
    }

    await createdOrderRecord.update((order: DemoOrderMaster) => {
      order.bpSalesProductsInformation = JSON.stringify(
        createdSalesRecords.map(record => record.id),
      );
      order.bpOrderMasterUpdatedAt = now;
    });

    await createdPaymentRecord.update((payment: DemoPaymentInformation) => {
      payment.bpOrderInformationId = createdOrderRecord.id;
      payment.bpPaymentUpdatedAt = now;
    });
  });

  if (!createdOrderId || !createdPaymentId) {
    throw new Error('Failed to create demo order records');
  }

  const createdOrderRecord = await orderCollection.find(createdOrderId);
  const createdPaymentRecord = await paymentCollection.find(createdPaymentId);
  const paymentObject =
    createdPaymentRecord.toPaymentObject() as DemoPaymentInformationObject;
  const salesObjects = createdSalesRecords.map(mapSaleModelToSalesInformation);
  const hydratedOrder = toHydratedOrder(
    createdOrderRecord,
    paymentObject,
    salesObjects,
  );

  return {
    order: hydratedOrder,
    checkoutPayload: toDemoCheckoutNavigationPayload(hydratedOrder),
  };
}

export async function getDemoOrderInformationById(
  orderId: string,
): Promise<DemoHydratedOrder | null> {
  try {
    const db = getDemoDatabase();
    const orderRecord = await db
      .get<DemoOrderMaster>('demo_order_master_information')
      .find(orderId);
    const salesRecords = await db
      .get<DemoSaleInformation>('demo_sales_information')
      .query(Q.where('bp_order_id', orderId))
      .fetch();
    const sortedSales = [...salesRecords].sort(
      (a, b) => a.bpOrderCreatedAt - b.bpOrderCreatedAt,
    );

    let paymentObject: DemoPaymentInformationObject = {
      _id: '',
      bp_payment_status: false,
      bp_payment_amount: orderRecord.bpOrderMasterOrderTotal,
      bp_payment_amount_received: 0,
      bp_payment_already_paid_amount: 0,
      bp_payment_amount_actually_received: 0,
      bp_payment_amount_due: orderRecord.bpOrderMasterOrderTotal,
      bp_payment_amount_refunded: 0,
      bp_payment_refunds: [],
      bp_order_information_id: orderRecord.id,
      bp_payment_created_at: orderRecord.bpOrderMasterCreatedAt,
      bp_payment_updated_at: orderRecord.bpOrderMasterUpdatedAt,
      bp_payment_deleted_at: 0,
      bp_payments: [],
      bp_payment_location_id: orderRecord.bpOrderMasterLocationId,
      bp_payment_method: 'Cash',
    };

    if (orderRecord.bpOrderMasterPaymentInformation) {
      try {
        const paymentRecord = await db
          .get<DemoPaymentInformation>('demo_payment_information')
          .find(orderRecord.bpOrderMasterPaymentInformation);
        paymentObject =
          paymentRecord.toPaymentObject() as DemoPaymentInformationObject;
      } catch {
        // Keep fallback payment object when payment record is unavailable.
      }
    }

    const salesObjects = sortedSales.map(mapSaleModelToSalesInformation);
    return toHydratedOrder(orderRecord, paymentObject, salesObjects);
  } catch (error) {
    console.error('[getDemoOrderInformationById] Error:', error);
    return null;
  }
}

export async function clearDemoCheckoutSessionData(): Promise<void> {
  const db = getDemoDatabase();
  await db.write(async () => {
    const cartItems = await db.get('demo_cart_items').query().fetch();
    const orderMasters = await db
      .get('demo_order_master_information')
      .query()
      .fetch();
    const payments = await db.get('demo_payment_information').query().fetch();
    const sales = await db.get('demo_sales_information').query().fetch();

    for (const record of [
      ...cartItems,
      ...orderMasters,
      ...payments,
      ...sales,
    ]) {
      await record.destroyPermanently();
    }
  });
}
